"use client";

import { useEffect, useSyncExternalStore } from "react";
import dynamic from "next/dynamic";
import { useSession } from "next-auth/react";
import { useLocale } from "next-intl";
import { SpinnerRound } from "spinners-react";
import { AnimatePresence } from "motion/react";
import { usePathname } from "@/i18n/navigation";
import { useMobileView } from "@/components/LargeContents/MessengerComponent/hooks/useMobileView";
import {
    closeDiscussionWindows,
    closeMessengerWindow,
    useCornerReserved,
    useMessengerWindowOwnerId,
    useMessengerWindows,
    isWindowExpanded,
    windowId,
    windowSlot,
    windowsThatFit,
} from "@/lib/messengerWindowStore";
import { FloatingWindowSlot } from "./FloatingWindowFrame";

const subscribeResize = (listener) => {
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
};
const useViewportWidth = () =>
    useSyncExternalStore(subscribeResize, () => window.innerWidth, () => 1280);

const loadDiscussionWindow = () => import("./MessengerDiscussionWindow");

// Le temps que le code de la fenêtre arrive, son cadre est déjà là
// (FloatingWindowSlot) : sans lui, le premier clic sur une discussion ne
// produisait rien à l'écran.
function WindowPlaceholder() {
    return (
        <div className="flex min-h-[64px] flex-1 items-center justify-center">
            <SpinnerRound size={28} thickness={160} color="#E8511D" />
        </div>
    );
}

const MessengerDiscussionWindow = dynamic(loadDiscussionWindow, {
    ssr: false,
    loading: WindowPlaceholder,
});

// La fenêtre où l'on écrit une question sur une session de cours : même
// cadre, même rangée, chargée à part pour ne pas embarquer le fil.
const DiscussionComposeWindow = dynamic(
    () => import("@/components/SessionDiscussions/DiscussionComposeWindow"),
    { ssr: false, loading: WindowPlaceholder },
);

// Appelée au survol de la bulle : le code de la fenêtre est déjà là au moment
// où l'on choisit une discussion.
export const preloadMessengerWindow = () => {
    loadDiscussionWindow().catch(() => {});
};

/**
 * Les fenêtres flottantes : les discussions ouvertes depuis la bulle de la
 * barre du haut et les questions en cours d'écriture sur une session,
 * rangées côte à côte en bas à droite sur ordinateur (trois au plus), une
 * seule en plein écran sur téléphone. Montées une fois à la racine, elles
 * survivent aux changements de page.
 *
 * Ce fichier ne porte que la coquille : tant qu'aucune discussion n'est
 * ouverte, il ne charge ni l'éditeur ni le temps réel.
 */
export default function MessengerFloatingWindow() {
    const { data: session, status } = useSession();
    const locale = useLocale();
    const pathname = usePathname() || "";
    const isMobileView = useMobileView();
    const windows = useMessengerWindows();
    const hasWindows = windows.length > 0;

    // La messagerie complète affiche déjà le fil : pas de doublon par-dessus.
    // Une question en cours d'écriture, elle, reste ouverte.
    const onMessengerPage = pathname.startsWith("/messenger");
    useEffect(() => {
        if (onMessengerPage) closeDiscussionWindows();
    }, [onMessengerPage]);

    // Les fenêtres retrouvées après un rechargement n'appartiennent qu'au
    // compte qui les avait ouvertes : déconnecté, ou connecté sous un autre
    // compte, on les oublie.
    const ownerId = useMessengerWindowOwnerId();
    const belongsToUser =
        session?.user && Number(ownerId) === Number(session.user.userId);
    const mustForget =
        hasWindows &&
        (status === "unauthenticated" ||
            (status === "authenticated" && !belongsToUser));
    useEffect(() => {
        if (mustForget) closeMessengerWindow();
    }, [mustForget]);

    const visible = Boolean(belongsToUser && hasWindows);
    // La carte des nouveautés occupe parfois le coin : la rangée se décale à
    // sa gauche. Si tout ne tient plus (trois fenêtres et la carte), les plus
    // anciennes sont masquées le temps que la carte soit fermée, pas fermées.
    const reserved = useCornerReserved();
    const viewportWidth = useViewportWidth();
    const open = onMessengerPage
        ? windows.filter((entry) => entry.compose)
        : windows;
    const shown = !visible
        ? []
        : isMobileView
          ? open.slice(-1)
          : windowsThatFit(open, viewportWidth, reserved);

    // Ce composant vit hors des layouts qui posent la classe `dark` et la
    // police : on les repose ici. Sur ordinateur, la rangée part du coin bas
    // droit, la plus récente à gauche ; elle ne capte aucun clic elle-même.
    return (
        <div
            // `right` n'agit que sur ordinateur, où la rangée est fixe.
            style={{ right: 16 + reserved }}
            className={`font-geistSans pointer-events-none md:fixed md:bottom-4 md:z-[70] md:flex md:flex-row-reverse md:items-end md:gap-3 md:transition-[right] md:duration-300 md:ease-out ${
                session?.user?.darkMode ? "dark" : ""
            }`}
        >
            <AnimatePresence>
                {shown.map((entry) => (
                    <FloatingWindowSlot
                        key={windowSlot(entry)}
                        contentKey={windowId(entry)}
                        minimized={entry.minimized && !isMobileView}
                        wide={isWindowExpanded(entry)}
                    >
                        {entry.compose ? (
                            <DiscussionComposeWindow
                                compose={entry.compose}
                                minimized={entry.minimized}
                                expanded={isWindowExpanded(entry)}
                                session={session}
                                locale={locale}
                            />
                        ) : (
                            <MessengerDiscussionWindow
                                discussion={entry.discussion}
                                minimized={entry.minimized}
                                expanded={isWindowExpanded(entry)}
                                session={session}
                                locale={locale}
                            />
                        )}
                    </FloatingWindowSlot>
                ))}
            </AnimatePresence>
        </div>
    );
}
